import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation } from '@reach/router';
import pt from 'prop-types';
import { buildWDISearchParams } from '/src/helpers/filters';
import { MicrocopyContext } from '/src/context/microcopy.context';
import { getCopy } from '/src/helpers/copy';
import { combine } from '/src/helpers/styles';
import { formatFilterLabel, getCopyWithFallback } from '/src/helpers/copy';
import Text from '../../../ui/text';
import Checkbox from '../../../ui/checkbox';
import Icon from '../../../ui/icon';
import Radiobutton from '../../../ui/radiobutton';
import * as styles from './filterSet.module.scss';

const getFilters = (queryFilters, visibleFilters, label) => {
  return visibleFilters.map((filter) => ({
    ...filter,
    checked: Array.isArray(queryFilters?.[filter.queryParam || label])
      ? queryFilters?.[filter.queryParam || label]?.includes(filter.value)
      : filter.queryParam === 'continents' && filter.value === ''
      ? [queryFilters?.continents, queryFilters?.countries].every(
          (queryFilter) => queryFilter === filter.value
        )
      : queryFilters?.[filter.queryParam || label] === filter.value,
  }));
};

const WDIFilterSet = ({
  label,
  visibleFilters,
  queryFilters,
  withButtonMargin,
  withTopBorder,
  isRadio,
  openAtStart,
  onChange,
  translatedLabels,
}) => {
  const microcopy = useContext(MicrocopyContext);
  const location = useLocation();
  const ref = useRef();
  const [isOpen, setIsOpen] = useState(openAtStart);
  const [maxHeight, setMaxHeight] = useState('none');
  const [filters, setFilters] = useState(
    getFilters(queryFilters, visibleFilters, label)
  );

  useEffect(() => {
    if (filters.find((el) => el.checked)) {
      setIsOpen(true);
    }
  }, [filters]);

  useEffect(() => {
    setFilters(getFilters(queryFilters, visibleFilters, label));
  }, [queryFilters, visibleFilters, label]);

  const handleFilterClick = onChange
    ? (filter) => {
        const newFilters = filters.map((f) =>
          f.value === filter.value ? { ...f, checked: !filter.checked } : f
        );
        setFilters(newFilters);
      }
    : () => {};

  useEffect(() => {
    if (onChange) {
      onChange(filters.filter((el) => el.checked).map((el) => el.value));
    }
  }, [filters]);

  const style = {
    transition: 'max-height 350ms ease-in-out',
    overflow: 'hidden',
    // For the collapse animation to work we need a fixed max-height,
    // therefore we simply pass the actual height of the element when the user collapses it (isOpen === false)
    maxHeight:
      maxHeight === 'none' && !isOpen ? ref.current?.clientHeight : maxHeight,
  };

  useEffect(() => {
    if (!isOpen) {
      setMaxHeight('0');
    } else {
      setMaxHeight(
        ref.current?.clientHeight ? `${ref.current?.clientHeight}px` : 'none'
      );
    }
  }, [isOpen]);

  const activeFilterCount = filters.filter((filter) => filter.checked).length;

  const Input = ({ children, filter }) => {
    if (!onChange) {
      const searchParams = buildWDISearchParams({
        filterKey: filter.queryParam || label,
        filter,
        isSingleValue: isRadio,
        location,
      }).toString();

      return (
        <a
          href={
            (searchParams ? `?${searchParams}` : location?.pathname) +
            '#resultList'
          }
          className={styles.filter}
          data-test={`filter-${label}-${filter.value}`}
        >
          {children}
        </a>
      );
    }

    return children;
  };

  return filters.filter((el) => !el.hide).length > 0 ? (
    <div
      className={combine(
        styles.filterSet,
        isOpen && styles.withMargin,
        withTopBorder && styles.withBorder
      )}
      data-test={`filterset-${label}`}
    >
      <button onClick={() => setIsOpen(!isOpen)} className={styles.header}>
        <Text as="span" type="metanavi" variant="semibold">
          {formatFilterLabel(
            translatedLabels && label !== 'filters.continents.all'
              ? label
              : getCopy(
                  `filters.${label}`.toLowerCase().replace(' ', ''),
                  microcopy
                ),
            activeFilterCount
          )}
        </Text>
        <Icon
          name={isOpen ? 'chevron-up' : 'chevron-down'}
          className={combine(
            styles.icon,
            withButtonMargin && styles.withMargin
          )}
          data-test={`filter-${label}`}
        />
      </button>
      <div
        style={style}
        onTransitionEnd={() => {
          if (isOpen) {
            // To avoid problems when resizing the screen we simply reset max-height to default after ended transition
            setMaxHeight('none');
          }
        }}
      >
        <div className={styles.filtersContainer} ref={ref}>
          <>
            {filters
              .sort((a, b) => a.sort - b.sort)
              .filter((filter) => !filter.hide)
              .map((filter) => {
                return (
                  <>
                    {filter.countries ? (
                      <>
                        <div className={styles.secondLevel}>
                          <WDIFilterSet
                            label={filter.label}
                            visibleFilters={filter.countries}
                            queryFilters={queryFilters}
                            isRadio
                            openAtStart={false}
                            translatedLabels
                          />
                        </div>
                      </>
                    ) : isRadio ? (
                      <Input filter={filter}>
                        <Radiobutton
                          label={getCopy(filter.label, microcopy)}
                          key={filter.value}
                          defaultChecked={filter.checked}
                          className={styles.isRadio}
                          onClick={() => handleFilterClick(filter)}
                          isLink
                        />
                      </Input>
                    ) : (
                      <Input filter={filter}>
                        <Checkbox
                          label={getCopy(filter.label, microcopy)}
                          key={filter.value}
                          defaultChecked={filter.checked}
                          onClick={() => handleFilterClick(filter)}
                          isLink
                        />
                      </Input>
                    )}
                  </>
                );
              })}
          </>
        </div>
      </div>
    </div>
  ) : null;
};

WDIFilterSet.propTypes = {
  label: pt.string.isRequired,
  visibleFilters: pt.arrayOf(
    pt.shape({
      name: pt.string,
      id: pt.oneOfType([pt.string, pt.number]),
    })
  ).isRequired,
  queryFilters: pt.arrayOf(pt.string),
  withButtonMargin: pt.bool,
  withTopBorder: pt.bool,
  openAtStart: pt.bool,
  type: pt.string,
};

WDIFilterSet.defaultProps = {
  withButtonMargin: false,
  withTopBorder: false,
  openAtStart: true,
  type: '',
};

export default WDIFilterSet;
